/**
 * TanStack Query hook for team skill coverage.
 *
 * Fetches the evaluated staffing rules and coverage gaps for a team over a
 * temporal window. In accordance with platform policy, query errors propagate
 * without synthetic fallbacks, retries, or client-side caching overrides, allowing
 * calling components to branch cleanly on HTTP statuses such as 403 ACCESS_DENIED.
 */

import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import {
  getTeamSkills,
  type GetTeamSkillsParams,
  type TeamSkillsResponse,
} from '@/api/endpoints/teamSkills';

export const teamSkillsKeys = {
  all: ['team-skills'] as const,
  lists: () => [...teamSkillsKeys.all, 'list'] as const,
  list: (teamId: string | undefined, params?: GetTeamSkillsParams) =>
    [...teamSkillsKeys.lists(), teamId, params] as const,
};

/**
 * Retrieve skill requirements and shift staffing coverage for a specific team.
 *
 * Execution is enabled only when a valid teamId is provided.
 */
export function useTeamSkills(teamId: string | undefined, period?: string) {
  return useAuthenticatedQuery<TeamSkillsResponse>({
    queryKey: teamSkillsKeys.list(teamId, period ? { period } : undefined),
    queryFn: () => {
      if (!teamId) {
        throw new Error('teamId is required to query team skills');
      }
      return getTeamSkills(teamId, period ? { period } : undefined);
    },
    enabled: !!teamId,
  });
}
